import React, { useState, useEffect } from 'react';
import { Mail, User, DollarSign, Check, X, Minus, Plus, Ticket } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import type { Campaign } from '../../lib/supabase';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

const ManualEntryForm: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    campaignId: '',
    email: '',
    name: '',
    numberOfTickets: 1,
  });
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchActiveCampaigns();
  }, []);

  useEffect(() => {
    const campaign = campaigns.find(c => c.id === formData.campaignId);
    setSelectedCampaign(campaign || null);
  }, [formData.campaignId, campaigns]);

  const fetchActiveCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .in('status', ['active', 'draft'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      setError('Failed to load campaigns');
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleTicketChange = (increment: number) => {
    setFormData(prev => ({
      ...prev,
      numberOfTickets: Math.max(1, Math.min(100, prev.numberOfTickets + increment))
    }));
  };

  const generateTicketNumber = (campaignId: string) => {
    const prefix = campaignId.slice(0, 4).toUpperCase();
    const random = Math.floor(100000 + Math.random() * 900000).toString();
    return `${prefix}-${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (!selectedCampaign) {
        throw new Error('Selected campaign not found');
      }

      // Create multiple entries
      const entries = Array.from({ length: formData.numberOfTickets }, () => ({
        campaign_id: formData.campaignId,
        email: formData.email.toLowerCase(),
        name: formData.name || null,
        ticket_number: generateTicketNumber(formData.campaignId),
        payment_method: 'inPerson',
        payment_status: 'paid',
        is_manual_entry: true,
        entered_by: user?.id
      }));

      const { error: entryError } = await supabase
        .from('entries')
        .insert(entries);

      if (entryError) throw entryError;

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating entries:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const totalCost = selectedCampaign ? selectedCampaign.ticket_price * formData.numberOfTickets : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Manual Entry</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-md">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Raffle
            </label>
            <select
              value={formData.campaignId}
              onChange={(e) => setFormData(prev => ({ ...prev, campaignId: e.target.value }))}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">Select a raffle...</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} - ${campaign.ticket_price.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Mail className="h-4 w-4 mr-2" />
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter email address"
              required
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 mr-2" />
              Name (Optional)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter participant's name"
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Ticket className="h-4 w-4 mr-2" />
              Number of Tickets
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => handleTicketChange(-1)}
                disabled={formData.numberOfTickets <= 1}
                className="p-2 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                value={formData.numberOfTickets}
                onChange={(e) => setFormData(prev => ({ ...prev, numberOfTickets: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) }))}
                className="w-20 text-center px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="1"
                max="100"
              />
              <button
                type="button"
                onClick={() => handleTicketChange(1)}
                disabled={formData.numberOfTickets >= 100}
                className="p-2 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {selectedCampaign && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-purple-800">Total Cost:</span>
                <span className="text-lg font-bold text-purple-800">
                  ${totalCost.toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-purple-600 mt-1">
                {formData.numberOfTickets} ticket{formData.numberOfTickets > 1 ? 's' : ''} × ${selectedCampaign.ticket_price.toFixed(2)}
              </p>
            </div>
          )}

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <DollarSign className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  All tickets will be automatically marked as paid. Make sure you've collected the full payment of ${totalCost.toFixed(2)} before submitting.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              {loading ? (
                'Processing...'
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Submit Entry
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualEntryForm;